/* eslint-disable brace-style */
import { init } from '@jarvis.oddle/core/tools/lib'
import uuid from 'uuid/v4'
import IconAmex from '@jarvis.oddle/core/components/Icons/IconAmex'
import IconDiners from '@jarvis.oddle/core/components/Icons/IconDiners'
import IconDiscover from '@jarvis.oddle/core/components/Icons/IconDiscover'
import IconJCB from '@jarvis.oddle/core/components/Icons/IconJCB'
import IconMastercard from '@jarvis.oddle/core/components/Icons/IconMastercard'
import IconUnionpay from '@jarvis.oddle/core/components/Icons/IconUnionpay'
import IconVisa from '@jarvis.oddle/core/components/Icons/IconVisa'
import { isObjectEmpty } from '@jarvis.oddle/core/tools/object'

export const paymentMethods = {
  APPLE_PAY: 'APPLE_PAY',
  FPX: 'FPX'
}

export const loadStripeScript = async () => {
  await init('https://js.stripe.com/v3/', 'stripe-js')
}

export const paymentTypes = {
  offline: 0,
  paypal: 2,
  taishin: 4,
  chinatrust: 5,
  braintree: 6,
  stripe: 7,
  omise: 8,
  paypalExpressCheckout: 10,
  veritrans: 11,
  ipay88: 12,
  paydollar: 13,
  paylah: 14,
  wirecard: 15,
  boost: 16,
  linePay: 17,
  oddleVoucher: 19,
  stripeExpressAccount: 20
}

const createStripePaymentMethod = async ({
  values,
  selectedPaymentMethod,
  stripe,
  stripeElement,
  newStripeApi
}) => {
  let token, tokenError
  if (newStripeApi) {
    /**
     * Generate required FPX paymentToken for OMS
     */
    if (selectedPaymentMethod.paymentMethod === paymentMethods.FPX) {
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'fpx',
        fpx: stripeElement
      })

      if (error) {
        tokenError = error
      } else {
        token = JSON.stringify({
          cardThreedSecure: null,
          paymentMethod: (paymentMethod.type || '').toUpperCase(),
          paymentMethodId: paymentMethod.id,
          card: null,
          country: null
        })
      }
    }
    /**
     * Generate required Apple Pay payment token for OMS
     */
    else if (selectedPaymentMethod.stripeRequestPayment) {
      token = values.stripeRequestPaymentToken
    }
    /**
     * Generate required card payment token for OMS
     */
    else {
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: stripeElement,
        billing_details: { name: values.cardName }
      })

      if (error) {
        tokenError = error
      } else {
        token = JSON.stringify({
          cardThreedSecure: paymentMethod.card.three_d_secure,
          paymentMethod: (paymentMethod.type || '').toUpperCase(),
          paymentMethodId: paymentMethod.id,
          card: paymentMethod.id,
          country: paymentMethod.card.country
        })
      }
    }
    /**
     * the methods bellow will be deprecated in the near future
     */
  } else {
    if (selectedPaymentMethod.useStripeSource) {
      const { source = { card: {} }, error } = await stripe.createSource({
        type: 'card',
        owner: { name: values.cardName }
      })

      if (error) {
        tokenError = error
      } else {
        token = JSON.stringify({
          cardThreedSecure: source.card.three_d_secure,
          card: source.id,
          country: source.card.country
        })

        tokenError = source.status !== 'chargeable'
          ? { message: 'stripe_not_chargeable' }
          : null
      }
    } else if (selectedPaymentMethod.stripeRequestPayment) {
      token = values.stripeRequestPaymentToken
    } else {
      const { token: stripeToken, error } = await stripe.createToken({ name: values.cardName })

      if (error) {
        tokenError = error
      } else {
        token = (stripeToken || {}).id
      }
    }
  }
  return { token, tokenError }
}

export const createPaymentToken = async ({
  values,
  redirectUrl,
  selectedPaymentMethod,
  stripe,
  stripeElement,
  newStripeApi,
  isFullyPaidByVoucher,
  isEnabledCustomerSaveCreditCardOnStripe,
  selectedSavedCreditCard
}) => {
  let token, tokenError

  if (isFullyPaidByVoucher) {
    token = redirectUrl
    return { token, tokenError }
  }

  switch (selectedPaymentMethod.type) {
    case paymentTypes.paypal:
    case paymentTypes.paypalExpressCheckout:
    case paymentTypes.ipay88:
    case paymentTypes.boost:
    case paymentTypes.linePay:
    case paymentTypes.wirecard:
      token = redirectUrl
      break
    case paymentTypes.chinatrust:
      if (selectedPaymentMethod.isEnabledChinatrustRedirect) {
        token = redirectUrl
      } else {
        const parts = values.card.cardExpiry.split(' / ')
        const expMonth = parts[0]
        const expYear = `20${parts[1]}`
        token = JSON.stringify({
          cardNo: values.card.cardNumber,
          cvv2: values.card.cvc,
          expYear,
          expMonth,
          redirectUrl
        })
      }
      break
    case paymentTypes.stripe:
      ({ token, tokenError } = await createStripePaymentMethod({
        values,
        selectedPaymentMethod,
        stripe,
        stripeElement,
        newStripeApi
      }))
      break
    case paymentTypes.stripeExpressAccount:
      if (
        isEnabledCustomerSaveCreditCardOnStripe &&
        !isObjectEmpty(selectedSavedCreditCard) &&
        selectedPaymentMethod.paymentMethod !== paymentMethods.FPX &&
        selectedPaymentMethod.stripeRequestPaymentButtonType !== paymentMethods.APPLE_PAY
      ) {
        token = JSON.stringify({
          cardThreedSecure: selectedSavedCreditCard.card.three_d_secure,
          paymentMethod: (selectedSavedCreditCard.type || '').toUpperCase(),
          paymentMethodId: selectedSavedCreditCard.id,
          card: selectedSavedCreditCard.id,
          country: selectedSavedCreditCard.card.country
        })
      } else {
        ({ token, tokenError } = await createStripePaymentMethod({
          values,
          selectedPaymentMethod,
          stripe,
          stripeElement,
          newStripeApi
        }))
      }
      break
  }

  return { token, tokenError }
}

export const extendPaymentMethodsWithStripe = async ({ paymentMethods = [], storeOrder, country, currency }) => {
  let extendedPaymentMethods = paymentMethods
  let stripePaymentRequestObject = null
  const stripePaymentMethod = paymentMethods.find(paymentMethod => paymentMethod.type === paymentTypes.stripe || paymentMethod.type === paymentTypes.stripeExpressAccount)

  // Check if can extend with Stripe Payment Request payment.
  if (stripePaymentMethod && stripePaymentMethod.isStripePaymentRequestButtonEnabled === true) {
    await loadStripeScript()
    const stripeObject = window.Stripe(stripePaymentMethod.paymentProcessToken)
    stripePaymentRequestObject = stripeObject.paymentRequest({
      country: country,
      currency: currency,
      total: {
        amount: storeOrder.totalAmountInSubUnit,
        label: storeOrder.menuName
      }
    })
    const canMakePayment = await stripePaymentRequestObject.canMakePayment()
    if (canMakePayment) {
      // Get all stripe payment methods avaiable.
      const stripePaymentMethods = []
      if (canMakePayment.applePay === true) {
        // Handle only Apple Pay for now.
        const applePayMethod = {
          ...stripePaymentMethod,
          paymentName: 'Apple Pay',
          stripeRequestPaymentButtonType: paymentMethods.APPLE_PAY,
          stripeRequestPayment: true
        }
        stripePaymentMethods.push(applePayMethod)
        extendedPaymentMethods = [...stripePaymentMethods, ...extendedPaymentMethods]
      }
    }
  }

  return {
    extendedPaymentMethods,
    stripePaymentRequestObject
  }
}

export const extendPaymentMethodsWithParsedIdField = (paymentMethods) => {
  // Add parseId field to support render selected method.
  return paymentMethods.map(method => {
    return { ...method, parsedId: uuid() }
  })
}

const iconCreditCard = {
  amex: IconAmex,
  diners: IconDiners,
  discover: IconDiscover,
  jcb: IconJCB,
  mastercard: IconMastercard,
  unionpay: IconUnionpay,
  visa: IconVisa
}

const brandCreditCard = {
  amex: 'Amex',
  diners: 'Diners',
  discover: 'Discover',
  jcb: 'JCB',
  mastercard: 'MasterCard',
  unionpay: 'UnionPay',
  visa: 'Visa'
}

export const getCreditCardInfo = (brand) => ({
  icon: iconCreditCard[brand],
  title: brandCreditCard[brand]
})
